<template>
  <PreviewBase
    v-if="type === 'document'"
    :loading="loading"
  >
    <div
      v-if="title"
      class="score"
    >
      <ScoreMeter
        :score="score"
        icon
        animate
      />
    </div>

    <div class="d-flex flex-column ga-1">
      <div
        v-if="title"
        class="d-flex justify-center"
      >
        <JobStatus :read-only="true" />
      </div>

      <div class="text-body-1 text-textDarkest text-center">
        {{ title }}
      </div>

      <div
        v-if="infoElements.length > 0"
        class="text-body-3 text-textLight d-flex justify-center ga-1"
      >
        <div
          v-for="info in infoElements"
          :key="info"
        >
          {{ info }}
        </div>
      </div>
    </div>

    <NoEditorWrapper>
      <div class="d-flex ga-2">
        <v-btn
          class="flex-grow-1"
          variant="outlined"
          color="primary"
          rounded="lg"
          :disabled="!title"
          @click="duplicateModalVisible = true"
        >
          Duplicate {{ startCase(camelCase(documentType)) }}
        </v-btn>

        <component
          :is="documentType === 'job_template' ? 'a' : 'router-link'"
          class="flex-grow-1"
          v-bind="documentType === 'job_template'
            ? { href: `/app/view/${id}`, target: '_blank' }
            : { to: { name: 'editor-view', params: { id } } }
          "
        >
          <v-btn
            class="w-100"
            variant="flat"
            color="primary"
            rounded="lg"
            @click="onOpenEditorClick"
          >
            Open in Editor
          </v-btn>
        </component>
      </div>
    </NoEditorWrapper>

    <div class="job-content">
      <div
        v-for="(field, i) in fieldsWithFooterAndHeaderJoined"
        :key="field.id"
      >
        <v-divider
          v-if="i > 0"
          class="opacity-100 mt-3 mb-4"
        />

        <div class="text-body-1 text-textDarkest">
          {{ field.label }}
        </div>

        <DocumentPreviewFieldHtml :html="field.html" />
      </div>
    </div>
    <DuplicateModal
      :show="duplicateModalVisible"
      :document-type="documentType"
      :doc-title="title"
      @close="duplicateModalVisible = false"
      @duplicate="onDuplicate"
    />
  </PreviewBase>
</template>

<script setup>
import { camelCase, startCase } from 'lodash-es';
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useEditor } from '@/stores/editor/editor';
import { useTracking } from '@/stores/tracking/tracking';
import { recruitInitEditorPreview } from '@/api/recruit';
import { vueGetPreview } from '@/api/vue';
import ScoreMeter from '@/components/editor/ScoreMeter.vue';
import JobStatus from '@/components/editor/JobStatus.vue';
import DuplicateModal from '@/components/shared/DuplicateModal.vue';
import { useLibrary } from '@/stores/library/library';
import { joinFooterAndHeaderFields } from '@/helpers/headerFooterFields';
import { useDocumentEditor } from '@/stores/document/editor';
import DocumentPreviewFieldHtml from '@ui/components/general/DocumentPreviewFieldHtml/DocumentPreviewFieldHtml.vue';
import PreviewBase from '@/components/shared/PreviewBase.vue';
import NoEditorWrapper from '@/components/editor/NoEditorWrapper.vue';

const route = useRoute();
const router = useRouter();
const { addErrorSnackbar } = useAppSnackbar();
const { setDocumentFields, setInitFields } = useEditor();
const { duplicateDocument } = useLibrary();
const { editorPreview: trackEditorPreview } = useTracking();

const {
  documentType,
  score,
  wordCount,
  revisionId,
  atsRequisitionId,
} = storeToRefs(useEditor());

const {
  fields,
  title,
  location,
  company,
  flexibleId,
} = storeToRefs(useDocumentEditor());

const id = computed(() => Number(route.query?.preview));
const type = computed(() => route.query?.type);
const loading = ref(false);
const duplicateModalVisible = ref(false);

const fieldsWithFooterAndHeaderJoined = computed(() => joinFooterAndHeaderFields(fields.value)
  .filter((field) => field.html.length > 20));

// take values of flexibleId, location and company refs,
// remove empty ones and add dot delimiter between each of them
const infoElements = computed(() => [
  company.value,
  location.value,
  flexibleId.value,
  atsRequisitionId.value ? `Req ${atsRequisitionId.value}` : '',
]
  .filter(Boolean)
  .flatMap((v) => ['·', v])
  .slice(1));

const onOpenEditorClick = () => {
  if (documentType.value !== 'job_template') {
    loading.value = true;
  }
};

const load = async () => {
  try {
    loading.value = true;
    setInitFields({});
    setDocumentFields({ documentData: {}, isRescore: false });

    const [recruitInitEditorResponse, vueGetResponse] = await Promise.all([
      recruitInitEditorPreview({ id: id.value }),
      vueGetPreview({ id: id.value }),
    ]);

    setInitFields(recruitInitEditorResponse?.body || {});
    setDocumentFields({ documentData: vueGetResponse, isRescore: false });

    trackEditorPreview({
      job: {
        jobId: id.value,
        company: company.value,
        title: title.value,
        location: location.value,
        score: score.value,
        wordCount: wordCount.value,
        revisionId: revisionId.value,
        documentType: documentType.value,
      },
    });
  } catch (e) {
    addErrorSnackbar({ text: `Load error - ${e.message}` });
    throw e;
  } finally {
    loading.value = false;
  }
};

watch(id, () => {
  if (id.value && type.value === 'document') {
    load();
  }
}, { immediate: true });

const onDuplicate = async ({ isPrivate, title: newTitle }) => {
  duplicateModalVisible.value = false;
  loading.value = true;

  const newJobId = await duplicateDocument({
    jobId: id.value,
    isPrivate,
    title: newTitle,
    from: 'preview',
  });

  return router.push({
    name: 'editor-view-reload',
    params: { id: newJobId },
    query: { ...route.query, preview: undefined, type: undefined },
  });
};
</script>

<style scoped lang="less">
.score {
  .score-meter-container {
    transform: scale(1.75);
    transform-origin: top center;
  }
  height: 64px;
  display: flex;
  justify-content: center;
}
</style>
